import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { VaultDistributionRecipientType } from '/@/modules/vaultdistributionsetting/enums/VaultDistributionRecipientType';
import { VaultAlternateDistributionRecipientType } from '/@/modules/vaultdistributionsetting/enums/VaultAlternateDistributionRecipientType';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { MinorDistributionInheritanceAgeType } from '@/modules/vaultdistributionsetting/constants/MinorDistributionInheritanceAgeType';

export class VaultDistributionSetting extends BaseEntityModel {
  /**
   * @var {string}
   */
  id;

  /**
   * @var {number}
   */
  estateId;

  /**
   * @var {string}
   */
  recipientType;

  /**
   * @var {boolean}
   */
  shouldRecalculate;

  /**
   * @var {string}
   */
  spouseAlternateRecipientType;

  /**
   * @var {string}
   */
  childrenAlternateRecipientType;

  /**
   * @var {string}
   */
  otherBeneficiaryAlternateRecipientType;

  /**
   * @var {string}
   */
  minorDistributionAgeType;

  /**
   * @var {number}
   */
  minorDistributionAge;

  /**
   * @var {boolean}
   */
  shouldApplyForMinorStepChildren;

  /**
   * @relationship
   * @var {EstateDistributionInheritanceAge[]}
   */
  estateDistributionInheritanceAges;

  /**
   * @relationship
   * @var {User[]}
   */
  administrators;

  /**
   * @relationship
   * @var {User[]}
   */
  alternateAdministrators;

  constructor(model = {}) {
    super(model);
    super.setRelationships(this, model, this.getModelName());

    this.id = model.id;
    this.estateId = model.estateId;
    this.recipientType = model.recipientType;
    this.shouldRecalculate = model.shouldRecalculate ?? false;
    this.spouseAlternateRecipientType = model.spouseAlternateRecipientType;
    this.childrenAlternateRecipientType = model.childrenAlternateRecipientType;
    this.otherBeneficiaryAlternateRecipientType = model.otherBeneficiaryAlternateRecipientType;
    this.minorDistributionAgeType = model.minorDistributionAgeType;
    this.minorDistributionAge = model.minorDistributionAge;
    this.shouldApplyForMinorStepChildren = model.shouldApplyForMinorStepChildren ?? false;
  }

  /**
   * @returns {boolean}
   */
  get isOnlySpouseRecipient() {
    return this.recipientType === VaultDistributionRecipientType.EVERYTHING_TO_MY_SPOUSE.key;
  }

  /**
   * @returns {boolean}
   */
  get isOnlyChildrenRecipient() {
    return (
      this.recipientType ===
      VaultDistributionRecipientType.EVERYTHING_TO_MY_EXISTING_AND_FUTURE_CHILDREN.key
    );
  }

  /**
   * @returns {boolean}
   */
  get isSpouseAndChildrenRecipients() {
    return (
      this.recipientType ===
      VaultDistributionRecipientType.HALF_TO_MY_SPOUSE_HALF_TO_MY_EXISTING_AND_FUTURE_CHILDREN.key
    );
  }

  /**
   * @returns {boolean}
   */
  get isOtherRecipient() {
    return this.recipientType === VaultDistributionRecipientType.OTHER.key;
  }

  /**
   * @returns {boolean}
   */
  get isSpouseRecipient() {
    return this.isOnlySpouseRecipient || this.isSpouseAndChildrenRecipients;
  }

  /**
   * @returns {boolean}
   */
  get isChildrenRecipient() {
    return this.isOnlyChildrenRecipient || this.isSpouseAndChildrenRecipients;
  }

  /**
   * @returns {boolean}
   */
  get isSpouseAlternateIsOther() {
    return this.spouseAlternateRecipientType === VaultAlternateDistributionRecipientType.OTHER.key;
  }

  /**
   * @returns {boolean}
   */
  get isChildrenAlternateIsOther() {
    return (
      this.childrenAlternateRecipientType === VaultAlternateDistributionRecipientType.OTHER.key
    );
  }

  /**
   * @returns {boolean}
   */
  get isOtherBeneficiaryAlternateIsOther() {
    return (
      this.otherBeneficiaryAlternateRecipientType ===
      VaultAlternateDistributionRecipientType.OTHER.key
    );
  }

  /**
   * @returns {string}
   */
  get minorDistributionAgeTypeByValue() {
    return (
      MinorDistributionInheritanceAgeType.getByKey(this.minorDistributionAgeType)?.value ?? null
    );
  }

  /**
   * @returns {boolean}
   */
  get isMultipleAgeInheritanceType() {
    return (
      this.minorDistributionAgeType ===
      MinorDistributionInheritanceAgeType.MULTIPLE_SPECIFIC_AGES.key
    );
  }

  /**
   * @returns {boolean}
   */
  get isMajorityAgeInheritanceType() {
    return this.minorDistributionAgeType === MinorDistributionInheritanceAgeType.MAJORITY.key;
  }

  /**
   * @returns {boolean}
   */
  get isSpecificAgeInheritanceType() {
    return this.minorDistributionAgeType === MinorDistributionInheritanceAgeType.SPECIFIC_AGE.key;
  }

  get lastSharePercentage() {
    const fullPercentage = 100;

    return !this.isMultipleAgeInheritanceType
      ? fullPercentage
      : fullPercentage - this.sumInheritanceAgeSharePercentages();
  }

  get displaySpouseAlternateRecipientType() {
    return VaultAlternateDistributionRecipientType.getByKey(this.spouseAlternateRecipientType)
      ?.value;
  }

  get displayChildrenAlternateRecipientType() {
    return VaultAlternateDistributionRecipientType.getByKey(this.childrenAlternateRecipientType)
      ?.value;
  }

  /**
   *
   * @returns {string}
   */
  getModelName() {
    return ModelType.VAULT_DISTRIBUTION_SETTING;
  }

  sumInheritanceAgeSharePercentages() {
    return this.estateDistributionInheritanceAges?.reduce(
      (sum, item) => sum + (item.sharePercentage || 0),
      0
    );
  }
}
